import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isEmpty from 'lodash/isEmpty';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { setModulePollingList, setTaskTrackerMap } from '../../../../store/investor/action';
import { unlockOrRefreshReport } from '../../../../store/investor/investorApiHelper';
import {
    TAB_TO_TASK_TRACKER_MAP,
    TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW,
    CROSS_MODULE_TASK_STEPS,
} from '../../../../utils/constants/commonConstants';

import { Button, CheckBox } from '../../../../common/_custom';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import SideDrawerFooter from '../../../../common/_custom/SideDrawer/components/SideDrawerFooter';

import greyLock from '../../../../assets/grey-lock-2.svg';
import { lockOpenRight } from '../../../../assets/hostedassets';
import styles from './RefreshReport.module.scss';
import { fetchCompanyOverviewData } from '../../../../store/dashboard/overview/actions';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { getSubdomain } from '../../../../utils/utils';
import { AICA_UNLOCK_REFRESH_UPDATE } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { Mixpanel } from '../../../../utils/mixpanel';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { useMcaPolling } from '../../../../utils/customHooks';
import { OverviewData } from '../../../../store/dashboard/overview/types';

dayjs.extend(relativeTime);

type Props = {
    close: () => void;
    onUnlockReportSuccess: () => void;
    selectedCompany: any;
};

const RefreshReport: React.FC<Props> = ({ close, onUnlockReportSuccess, selectedCompany }) => {
    const dispatch = useAppDispatch();
    const params = useParams();

    const [overviewData, setOverviewData] = useState<OverviewData | null>(null);

    const orgId: string = params.id as string;
    const user = useAppSelector((state) => state.user);
    const dashboardConfig: any = useAppSelector((state) => state.investor.config);
    const permissions = useAppSelector((state) => state.investor.permissions);
    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus);
    const taskTrackerMap = useAppSelector((state) => state.investor.taskTrackerMap);
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const investorData = useAppSelector((state) => state.investor);
    const showNewOverview =
        useAppSelector((state) => state.dashboard.dashboardReducer.config?.tabConfigs?.overview?.showNewOverview) || false;
    const selectedCompanyFromRedux = useAppSelector((state) => state.investor.selectedCompany);
    const profileGenerationStatus = investorData?.selectedCompany?.profileGenerationStatus;

    const { mcaDocData } = useMcaPolling(orgId);

    const onboardingTabs = permissions?.onboardingTabs;
    const isProfileLocked = profileGenerationStatus === 'LOCKED';
    const allRefreshableModules = dashboardConfig?.tabs?.filter((tab: any) => tab.refreshableModule === true);
    const primaryModuleRefreshList =
        dashboardConfig?.tabs?.filter((tab: any) => tab.refreshableModule === true && tab.category === 'primary') || [];
    const [unlocking, setUnlocking] = useState(false);
    const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // Get overallStatus from Redux state instead of making API call
    const overallStatus = selectedCompanyFromRedux?.overallStatus || '';

    /**
     * Determines if a tab is locked based on multiple factors:
     * - If overview tab and overallStatus is LOCKED, always lock the overview tab
     * - For primary modules, trust the tabLockStatus from Polus
     * - For secondary modules, check task tracker evidence to determine if unlocked; if no evidence, treat as locked
     */
    const isTabLocked = useCallback(
        (tabId: string) => {
            // Priority check: If overview tab and overallStatus is LOCKED, always lock the overview tab
            if (tabId === 'overview' && overallStatus === 'LOCKED') {
                return true;
            }

            // Primary modules (Banking etc.) — trust polus lock status, default to locked if no data yet
            if (primaryModuleRefreshList.some((tab: any) => tab.id === tabId)) {
                const polusEntry = tabLockStatus?.[tabId];
                if (polusEntry == null) return TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW.includes(tabId);
                return !!polusEntry.isLocked;
            }

            // Secondary modules — check task tracker for unlock evidence
            const steps = TAB_TO_TASK_TRACKER_MAP[tabId] || [];
            let anyStepCompleteOrFailed = false;
            if (!isEmpty(taskTrackerMap)) {
                const taskTrackerSteps = steps.filter((step) => step in taskTrackerMap).map((step) => taskTrackerMap[step].status);
                anyStepCompleteOrFailed =
                    !isEmpty(taskTrackerSteps) &&
                    taskTrackerSteps.some((step) => step === 'COMPLETE' || step === 'FAILED' || step === 'FAILURE');
            }

            // If any step completed/failed, module is unlocked
            if (anyStepCompleteOrFailed) return false;

            // Otherwise locked: either profile is locked, no task tracker evidence, or polus says locked
            return true;
        },
        [isProfileLocked, onboardingTabs, taskTrackerMap, tabLockStatus, overallStatus],
    );

    useEffect(() => {
        dispatch(
            fetchCompanyOverviewData({
                assesseeOrgId: orgId,
                investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                onSuccess: (data: OverviewData, actionCta) => {
                    setOverviewData(data);
                },
            }),
        );
    }, [orgId, user.domainMetadata?.investorOrgId]);

    const handleModuleSelection = (module: string) => {
        const newSelectedModules = new Set<string>(selectedModules);

        if (module === 'all') {
            if (selectedModules.size === allRefreshableModules.length) {
                newSelectedModules.clear();
            } else {
                allRefreshableModules.forEach((module) => newSelectedModules.add(module.id));
            }
        } else if (selectedModules.has(module)) {
            newSelectedModules.delete(module);
        } else {
            newSelectedModules.add(module);
        }

        setSelectedModules(newSelectedModules);
    };

    const onPrimary = () => {
        Mixpanel.track(AICA_UNLOCK_REFRESH_UPDATE, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userData?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
            modulesForUnlock: Array.from(selectedModules).filter((module) => isTabLocked(module)),
            modulesForRefresh: Array.from(selectedModules).filter((module) => !isTabLocked(module)),
            modulesCountForUpdate: selectedModules.size,
        });

        setUnlocking(true);

        unlockOrRefreshReport(
            {
                assesseeOrgId: orgId,
                investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                tabs: Array.from(selectedModules),
            },
            {
                onSuccess: () => {
                    setUnlocking(false);
                    const affectedModules = new Set<string>(selectedModules);
                    selectedModules.forEach((id) => {
                        const tab = dashboardConfig?.tabs?.find((t: any) => t.id === id);
                        const subModuleIds = (tab?.subModules || [])
                            .map((name: string) => dashboardConfig?.tabs?.find((t: any) => t.label === name)?.id)
                            .filter(Boolean);
                        subModuleIds.forEach((t: string) => affectedModules.add(t));
                    });
                    dispatch(setTaskTrackerMap({}));
                    dispatch(setModulePollingList(Array.from(affectedModules)));
                    if (profileGenerationStatus === 'LOCKED') {
                        onUnlockReportSuccess();
                    }
                    dispatch(
                        fetchCompanyOverviewData({
                            assesseeOrgId: orgId,
                            investorOrgId: user.domainMetadata?.investorOrgId ?? '',
                            onSuccess: () => {
                                updateQueryParams(searchParams, setSearchParams, { triggerMain: true }, location);
                            },
                            onError: () => {},
                        }),
                    );
                    close();
                },
                onError: () => {
                    setUnlocking(false);
                },
            },
        ).finally(() => {
            setUnlocking(false);
        });
    };

    /* 
        Filters out cross-module task steps from each module's step list to compute exclusive steps.
        Used for last_fetched date — prevents one module's refresh from updating another module's date.
    */
    const exclusiveStepsMap = useMemo(() => {
        const result: Record<string, string[]> = {};
        const refreshableIds = (allRefreshableModules || []).map((m: any) => m.id);
        refreshableIds.forEach((id: string) => {
            result[id] = (TAB_TO_TASK_TRACKER_MAP[id] || []).filter((step) => !CROSS_MODULE_TASK_STEPS.has(step));
        });
        return result;
    }, [allRefreshableModules]);

    const renderModuleSelectionTable = useMemo(() => {
        return (
            <table className={styles.ModuleSelectionTable}>
                <thead className={styles.ModuleSelectionTableHead}>
                    <tr>
                        <th style={{ width: '56px' }}>
                            <CheckBox
                                checked={selectedModules.size === allRefreshableModules.length}
                                label=""
                                onCheck={() => handleModuleSelection('all')}
                                variant="primary"
                            />
                        </th>
                        <th style={{ width: '19.188rem' }}>List of modules</th>
                        <th>Status</th>
                        <th>Last fetched</th>
                    </tr>
                </thead>

                <tbody className={styles.ModuleSelectionTableBody}>
                    {allRefreshableModules.map((module) => {
                        let lastFetchedInWords = '-';
                        const moduleTasks = TAB_TO_TASK_TRACKER_MAP[module.id] || [];
                        const exclusiveTasks = exclusiveStepsMap[module.id] || [];
                        const locked = isTabLocked(module.id);

                        // Only show last fetched if the module itself is unlocked.
                        // Prefer exclusive steps to prevent cross-module date leaks.
                        if (!locked && taskTrackerMap && moduleTasks.length) {
                            const exclusiveFetched = exclusiveTasks
                                .filter((task) => taskTrackerMap[task]?.status === 'COMPLETE')
                                .map((task) => taskTrackerMap[task]?.dateCreated)
                                .filter(Boolean)
                                .sort((a, b) => b - a)[0];
                            const lastFetched =
                                exclusiveFetched ||
                                moduleTasks
                                    .filter((task) => taskTrackerMap[task]?.status === 'COMPLETE')
                                    .map((task) => taskTrackerMap[task]?.dateCreated)
                                    .filter(Boolean)
                                    .sort((a, b) => b - a)[0];
                            lastFetchedInWords = lastFetched ? dayjs(lastFetched).fromNow() : '-';
                        }

                        const isFinancialsModule = module.id === 'auditedFinancials';
                        const isProcessing =
                            isFinancialsModule && mcaDocData?.reportStatus === 'Processing' && mcaDocData?.isFetching === true;

                        const subModules = module?.subModules;
                        const moreCount = subModules?.length > 1 ? subModules.length - 1 : 0;
                        const subModulesText = subModules?.length ? (
                            <>
                                ({subModules[0]} {moreCount > 0 && <span className={styles.SubModulesMore}> +{moreCount} more</span>})
                            </>
                        ) : null;
                        const isSelected = selectedModules.has(module.id);

                        return (
                            <tr key={module.id} className={isSelected ? styles.SelectedRow : ''}>
                                <td>
                                    <CheckBox
                                        checked={isSelected}
                                        label=""
                                        onCheck={() => handleModuleSelection(module.id)}
                                        variant="primary"
                                    />
                                </td>
                                <td>
                                    <div className={styles.ModuleName}>
                                        <div className={styles.ModuleLabel}>
                                            <span>{module.label}</span>
                                            {subModulesText &&
                                                (subModules?.length > 1 ? (
                                                    <Tooltip
                                                        content={subModules.join(', ')}
                                                        direction="top"
                                                        usePortal={true}
                                                        fitContent
                                                        style={{ maxWidth: '15rem', maxHeight: '3.5rem' }}
                                                    >
                                                        <span className={styles.SubModulesSummary}>{subModulesText}</span>
                                                    </Tooltip>
                                                ) : (
                                                    <span className={styles.SubModulesSummary}>{subModulesText}</span>
                                                ))}
                                        </div>
                                        {isProcessing && <LoadingSpinner color="#6021b3" height="15px" className={styles.InlineSpinner} />}
                                    </div>
                                </td>
                                <td>
                                    {modulePollingList.includes(module?.id as string) ? (
                                        <LoadingSpinner color="#6021b3" height="15px" />
                                    ) : locked ? (
                                        <div className={styles.StatusLocked}>
                                            <span>Locked</span>
                                            <img src={greyLock} alt="" width="14" height="14" />
                                        </div>
                                    ) : (
                                        <div className={styles.StatusUnlocked}>
                                            <span>Unlocked</span>
                                            <img src={lockOpenRight} alt="" width="12" height="12" />
                                        </div>
                                    )}
                                </td>
                                <td className={styles.LastFetched}>
                                    {isProcessing ? (
                                        <span className={styles.FetchingText}>Fetching latest data from MCA (takes upto 24 hours)</span>
                                    ) : modulePollingList.includes(module?.id as string) ? (
                                        <span className={styles.FetchingText}>Fetching…</span>
                                    ) : (
                                        lastFetchedInWords
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }, [allRefreshableModules, selectedModules, taskTrackerMap, mcaDocData, modulePollingList, tabLockStatus]);

    return (
        <SideDrawer
            showHeading
            heading={<div className={styles.DrawerHeader}>Unlock/Refresh Report</div>}
            onClose={() => close()}
            width="44.438rem"
        >
            <div className={styles.RefreshReportContainer} style={{ height: '100%' }}>
                <div className={styles.RefreshReportContent}>
                    {renderModuleSelectionTable}
                    <p className={styles.RefrestNote}>
                        User consent-based data from GST, Debt Schedule etc. will be updated automatically when available.
                    </p>
                </div>

                <SideDrawerFooter>
                    <div className={styles.FooterContainer}>
                        <Button
                            style={{ marginTop: 0 }}
                            type="submit"
                            disabled={selectedModules.size === 0}
                            loading={unlocking}
                            onClick={onPrimary}
                            variant="primary"
                            text="Update"
                        />
                        <Button style={{ marginTop: 0 }} type="submit" onClick={close} variant="secondary" text="Cancel" />
                    </div>
                </SideDrawerFooter>
            </div>
        </SideDrawer>
    );
};

export default RefreshReport;
